import { Fragment, type FC, type ReactNode } from 'react';
import { WikiLink as Link } from '../components/WikiLink';
import { cn } from '../../../utils/cn';
import { wikiHref } from '../wikiText';
import { WikiName } from '../components/WikiName';

import { type WikiBlock, type WikiInline, wikiLinkTarget } from './parseWikiMarkdown';
import type { Language } from '../../../i18n/types';

const renderInline = (nodes: WikiInline[], lang: Language, inLink = false): ReactNode[] =>
  nodes.map((node, i) => {
    if (node.type === 'text') {
      return <WikiName key={i} name={node.text} mode={inLink ? 'name' : 'text'} />;
    }
    if (node.type === 'strong') return <strong key={i} className="font-semibold text-label-primary">{renderInline(node.children, lang, inLink)}</strong>;
    if (node.type === 'em') return <em key={i}>{renderInline(node.children, lang, inLink)}</em>;
    const target = wikiLinkTarget(node.href);
    const children = renderInline(node.children, lang, true);
    if (target.kind === 'internal') {
      return (
        <Link key={i} href={target.path.startsWith('#') ? target.path : wikiHref(lang, target.path)} className="text-accent-blue hover:underline">
          {children}
        </Link>
      );
    }
    if (target.kind === 'external') {
      return (
        <a key={i} href={target.url} target="_blank" rel="noopener noreferrer" className="text-accent-blue hover:underline">
          {children}
        </a>
      );
    }
    return <span key={i}>{children}</span>;
  });

interface WikiMarkdownProps {
  blocks: WikiBlock[];
  lang: Language;
  insert?: WikiMarkdownInsert;
  className?: string;
}

export type WikiMarkdownInsert = { after: number; node: ReactNode };

export const WikiMarkdown: FC<WikiMarkdownProps> = ({ blocks, lang, insert, className }) => (
  <div className={cn('flex flex-col gap-4 text-[16px] leading-[26px] text-white/90', className)}>
    {blocks.map((block, i) =>
      insert?.after === i ? (
        <Fragment key={i}>
          <WikiBlockView block={block} lang={lang} />
          {insert.node}
        </Fragment>
      ) : (
        <WikiBlockView key={i} block={block} lang={lang} />
      ),
    )}
  </div>
);

const WikiBlockView: FC<{ block: WikiBlock; lang: Language }> = ({ block, lang }) => {
  if (block.type === 'heading') {
    const Heading = block.level === 2 ? 'h2' : 'h3';
    return (
      <Heading
        id={block.id}
        className={cn(
          'm-0 scroll-mt-4 font-bold text-label-primary [text-wrap:balance]',
          block.level === 2 ? 'mt-4 text-[22px] leading-[28px] first:mt-0' : 'mt-2 text-[18px] leading-[24px] first:mt-0',
        )}
      >
        {renderInline(block.children, lang)}
      </Heading>
    );
  }
  if (block.type === 'list') {
    const List = block.ordered ? 'ol' : 'ul';
    return (
      <List className={cn('m-0 flex flex-col gap-1.5 ps-6', block.ordered ? 'list-decimal' : 'list-disc', 'marker:text-label-secondary')}>
        {block.items.map((item, j) => (
          <li key={j}>{renderInline(item, lang)}</li>
        ))}
      </List>
    );
  }
  return <p className="m-0">{renderInline(block.children, lang)}</p>;
};
